/**
 * Phase 138H.b — Academy Owner Fee-Dunning Control Panel.
 *
 * Self-service surface over the Phase 138H runner:
 *   • Toggle `auto_fee_dunning_enabled` for this academy
 *   • Preview the next sweep (dry-run, no messages sent)
 *   • Inspect the historical audit stream (which parents got which kind, when)
 *
 * Wires to:
 *   GET  /api/academy/settings/auto-fee-dunning
 *   PUT  /api/academy/settings/auto-fee-dunning
 *   GET  /api/academy/billing/dunning-logs
 *   POST /api/academy/billing/dunning-preview
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Radio, Eye, RefreshCw, Mail, MessageCircle, ArrowLeft,
  ShieldCheck, ShieldOff, Clock3, AlertTriangle, CheckCircle2,
  IndianRupee, Inbox,
} from 'lucide-react';

const CADENCE_STEPS = [
  { key: 'pre_due', offset: '-1d', label: 'Pre-Due Nudge', tone: 'emerald',
    desc: 'Friendly heads-up 24h before the due date.' },
  { key: 'due_day', offset: 'T+0', label: 'Due Day Reminder', tone: 'amber',
    desc: 'Confirms the bill is live today.' },
  { key: 'followup_1', offset: '+2d', label: 'Follow-Up Tier 1', tone: 'orange',
    desc: 'First overdue nudge, tone shifts firmer.' },
  { key: 'followup_2', offset: '+4d', label: 'Follow-Up Tier 2', tone: 'orange',
    desc: 'Second overdue reminder.' },
  { key: 'followup_3', offset: '+6d', label: 'Final Notice', tone: 'rose',
    desc: 'Auto-silences after this — staff takes over.' },
];

const KIND_TONE = {
  pre_due: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  due_day: 'bg-amber-50 text-amber-700 border border-amber-200',
  followup_1: 'bg-orange-50 text-orange-700 border border-orange-200',
  followup_2: 'bg-orange-100 text-orange-800 border border-orange-300',
  followup_3: 'bg-rose-50 text-rose-700 border border-rose-200',
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
};

export default function DunningControlPanel() {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({
    total_rows: 0,
    kinds_breakdown: {},
    channels_breakdown: { email: 0, whatsapp: 0 },
  });
  const [logsLoading, setLogsLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadToggle = useCallback(async () => {
    try {
      const { data } = await api.get('/academy/settings/auto-fee-dunning');
      setEnabled(Boolean(data?.enabled));
    } catch (e) {
      // Stay silent on initial load — the toggle just renders as off
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get('/academy/billing/dunning-logs', {
        params: { limit: 200 },
      });
      setLogs(data?.audit_entries || []);
      setSummary(data?.summary || {
        total_rows: 0, kinds_breakdown: {}, channels_breakdown: {},
      });
    } catch (e) {
      toast.error('Could not load dunning audit stream');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const handleToggle = async (next) => {
    setToggling(true);
    try {
      await api.put('/academy/settings/auto-fee-dunning', { enabled: next });
      setEnabled(next);
      toast.success(next
        ? 'Automated dunning sweeps re-enabled'
        : 'Automation paused — staff handles outreach manually');
    } catch (e) {
      toast.error('Could not update the setting. Try again.');
    } finally {
      setToggling(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post('/academy/billing/dunning-preview');
      setPreviewData(data);
      const total = (data?.pending_count || 0) + (data?.already_dispatched_count || 0);
      if (total === 0) {
        toast.info('No reminders pending for the next sweep.');
      } else {
        toast.success(`${data.pending_count} reminder${data.pending_count === 1 ? '' : 's'} queued for the next 09:30 IST sweep`);
      }
    } catch (e) {
      toast.error('Could not preview the next sweep');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadToggle();
    loadLogs();
  }, [loadToggle, loadLogs]);

  const totalDispatched = summary.total_rows || 0;
  const emailHits = summary.channels_breakdown?.email || 0;
  const whatsappHits = summary.channels_breakdown?.whatsapp || 0;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 space-y-6"
         data-testid="dunning-control-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <button
            onClick={() => navigate('/finance')}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 font-medium transition-colors"
            data-testid="dunning-back-to-finance">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Finance
          </button>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
            Fee Dunning Control Center
          </h1>
          <p className="text-sm text-slate-500 max-w-xl">
            Automate gentle WhatsApp + Email reminders for outstanding student fees,
            on a 5-step cadence. Magic-link checkout means parents settle in one tap.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <Badge
            variant="outline"
            className={enabled
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 px-3 py-1'
              : 'bg-slate-100 text-slate-600 border-slate-300 px-3 py-1'}
            data-testid="dunning-status-badge">
            {enabled
              ? <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Cycle Active</>
              : <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Cycle Paused</>}
          </Badge>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card data-testid="dunning-tile-total">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Reminders Dispatched
            </div>
            <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums">
              {totalDispatched}
            </div>
            <div className="text-[11px] text-slate-500">All-time</div>
          </CardContent>
        </Card>
        <Card data-testid="dunning-tile-email">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Email Touches
            </div>
            <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums flex items-center gap-1.5">
              <Mail className="w-5 h-5 text-indigo-600" /> {emailHits}
            </div>
            <div className="text-[11px] text-slate-500">Via Resend</div>
          </CardContent>
        </Card>
        <Card data-testid="dunning-tile-whatsapp">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              WhatsApp Touches
            </div>
            <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums flex items-center gap-1.5">
              <MessageCircle className="w-5 h-5 text-emerald-600" /> {whatsappHits}
            </div>
            <div className="text-[11px] text-slate-500">Via Twilio</div>
          </CardContent>
        </Card>
        <Card data-testid="dunning-tile-cadence-cap">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Cadence Cap
            </div>
            <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums">
              6d
            </div>
            <div className="text-[11px] text-slate-500">Auto-silences after</div>
          </CardContent>
        </Card>
      </div>

      {/* Two-column: Cadence + Preview | Audit Stream */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* LEFT: Toggle + Cadence + Preview */}
        <div className="space-y-4 md:space-y-6">
          {/* Toggle Card */}
          <Card data-testid="dunning-toggle-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                <Radio className={`w-4 h-4 ${enabled ? 'text-emerald-600 animate-pulse' : 'text-slate-400'}`} />
                Automation Switch
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white">
                <div className="space-y-0.5 flex-1">
                  <div className="text-sm font-bold text-slate-900">
                    Auto Fee Dunning
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed">
                    Runs daily at <b>09:30 IST</b>. When OFF, no reminders are sent —
                    your team handles collections manually.
                  </div>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={handleToggle}
                  disabled={toggling}
                  data-testid="dunning-auto-toggle"
                />
              </div>
              {!enabled && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-[11px] text-amber-900 leading-relaxed">
                    Automation paused. New overdue fees will <b>not</b> trigger any
                    reminders until you switch this back on.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cadence Sequence */}
          <Card data-testid="dunning-cadence-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                <Clock3 className="w-4 h-4 text-indigo-600" />
                5-Step Cadence Sequence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {CADENCE_STEPS.map((step) => (
                <div key={step.key}
                     className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                     data-testid={`cadence-step-${step.key}`}>
                  <div className={`text-[10px] font-black tabular-nums px-2 py-0.5 rounded ${KIND_TONE[step.key]}`}>
                    {step.offset}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-900">
                      {step.label}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-snug">
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
              <Button
                onClick={runPreview}
                disabled={previewLoading}
                className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold"
                data-testid="dunning-preview-btn">
                {previewLoading
                  ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Simulating…</>
                  : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Simulate Next Sweep</>}
              </Button>
              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                Dry-run only — no emails or WhatsApp messages will be sent.
              </p>
            </CardContent>
          </Card>

          {/* Preview Result */}
          {previewData && (
            <Card data-testid="dunning-preview-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-700" /> Sweep Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5">
                    <div className="text-[10px] font-black uppercase text-emerald-700">
                      Will Fire
                    </div>
                    <div className="text-xl font-black text-slate-900 tabular-nums">
                      {previewData.pending_count}
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                    <div className="text-[10px] font-black uppercase text-slate-600">
                      Already Sent Today
                    </div>
                    <div className="text-xl font-black text-slate-900 tabular-nums">
                      {previewData.already_dispatched_count}
                    </div>
                  </div>
                </div>
                {(previewData.pending_reminders || []).length === 0 ? (
                  <div className="text-center text-[11px] text-slate-400 py-3 italic">
                    Nothing queued for the next sweep.
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {previewData.pending_reminders.map((r) => (
                      <div key={`${r.fee_id}-${r.kind}`}
                           className="flex items-center justify-between gap-2 p-2 rounded-md border border-slate-200 bg-white text-xs"
                           data-testid={`preview-row-${r.fee_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate">
                            {r.student_name}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {formatINR(r.amount)} · due {r.due_date}
                          </div>
                        </div>
                        <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${KIND_TONE[r.kind] || 'bg-slate-100'}`}>
                          {r.kind}
                        </span>
                        {r.already_dispatched_today && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Audit Stream */}
        <div className="lg:col-span-2">
          <Card className="h-full" data-testid="dunning-audit-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                <Inbox className="w-4 h-4 text-slate-700" />
                Communication Audit Stream
              </CardTitle>
              <Button
                size="sm" variant="ghost"
                onClick={loadLogs}
                disabled={logsLoading}
                className="h-7 text-xs"
                data-testid="dunning-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="space-y-2 p-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : logs.length === 0 ? (
                <div className="p-10 text-center space-y-2">
                  <Inbox className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="text-sm font-bold text-slate-700">
                    No reminders dispatched yet
                  </div>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                    Once a student fee crosses a cadence checkpoint, every
                    reminder fired by the system shows up here with full
                    channel + magic-link audit trail.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
                  {logs.map((log) => (
                    <div key={log.id}
                         className="p-3.5 hover:bg-slate-50/80 transition-colors"
                         data-testid={`audit-row-${log.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-bold text-sm text-slate-900 truncate">
                              {log.student_name}
                            </div>
                            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${KIND_TONE[log.kind] || 'bg-slate-100 text-slate-700'}`}>
                              {log.kind?.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {log.guardian_name ? `${log.guardian_name} · ` : ''}
                            {log.parent_contact}
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-slate-500">
                            <span className="inline-flex items-center gap-1 font-bold text-slate-700">
                              <IndianRupee className="w-3 h-3" />
                              {formatINR(log.amount).replace('₹', '')}
                            </span>
                            <span className="text-slate-300">·</span>
                            <span>{formatTimestamp(log.dispatched_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {(log.channels_succeeded || []).includes('email') && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                              <Mail className="w-3 h-3" /> Email
                            </span>
                          )}
                          {(log.channels_succeeded || []).includes('whatsapp') && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                              <MessageCircle className="w-3 h-3" /> WhatsApp
                            </span>
                          )}
                          {(log.channels_attempted || []).length > 0 &&
                           (log.channels_succeeded || []).length === 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                              <AlertTriangle className="w-3 h-3" /> Failed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
